import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiDownload, FiArchive, FiFileText, FiCheckCircle, FiAlertCircle, FiClock } from 'react-icons/fi';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import PageLayout from '../../components/PageLayout';

const Backup = () => {
  const { activeCompany, companyUser, currentUser } = useAuth();
  // SAAS-04: only the owner or a company admin may download company data
  // (the server refuses everyone else too).
  const canBackup = companyUser?.role === 'admin'
    || (!!activeCompany?.ownerId && activeCompany.ownerId === currentUser?.uid);

  const [backupState, setBackupState] = useState({ loading: false, result: null, error: '' });
  const [exportState, setExportState] = useState({ loading: false, result: null, error: '' });

  const handleCreateBackup = async () => {
    if (!activeCompany?.id) {
      setBackupState({ loading: false, result: null, error: 'No active company.' });
      return;
    }
    setBackupState({ loading: true, result: null, error: '' });
    try {
      const fn = httpsCallable(functions, 'createBackupJson');
      const { data } = await fn({ companyId: activeCompany.id });
      setBackupState({ loading: false, result: data, error: '' });
      // auto-trigger download
      const a = document.createElement('a');
      a.href = data.url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Backup error:', err);
      setBackupState({ loading: false, result: null, error: err.message || 'Backup failed' });
    }
  };

  const handleExportExcel = async () => {
    if (!activeCompany?.id) {
      setExportState({ loading: false, result: null, error: 'No active company.' });
      return;
    }
    setExportState({ loading: true, result: null, error: '' });
    try {
      const fn = httpsCallable(functions, 'exportDataExcel');
      const { data } = await fn({ companyId: activeCompany.id });
      setExportState({ loading: false, result: data, error: '' });
      const a = document.createElement('a');
      a.href = data.url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export error:', err);
      setExportState({ loading: false, result: null, error: err.message || 'Export failed' });
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  if (!canBackup) {
    return (
      <PageLayout pageTitle="Backup & Export" breadcrumb="Settings / Backup">
        <Container>
          <Header>
            <Title>Backup & Export</Title>
            <Subtitle>Only the company owner or an admin can download or export company data.</Subtitle>
          </Header>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout pageTitle="Backup & Export" breadcrumb="Settings / Backup">
      <Container>
        <Header>
          <Title>Backup & Export</Title>
          <Subtitle>Download a copy of your company's data. Keep it safe — it's your responsibility to store backups securely.</Subtitle>
        </Header>

        <CardGrid>
          {/* Create Backup (JSON) */}
          <Card as={motion.div} whileHover={{ y: -2 }}>
            <CardIcon $color={colors.primary.main}>
              <FiArchive />
            </CardIcon>
            <CardTitle>Create Backup</CardTitle>
            <CardDescription>
              Full snapshot of every record in your company — items, customers, vendors, transactions, settings, and everything else. Saved as a <strong>.zip of JSON files</strong>.
            </CardDescription>
            <BulletList>
              <Bullet>For disaster recovery — send to support if you ever need to restore.</Bullet>
              <Bullet>Complete and machine-readable. Not meant to be opened by humans.</Bullet>
              <Bullet>Image/file attachments are not included (only their links).</Bullet>
            </BulletList>

            <ActionButton onClick={handleCreateBackup} disabled={backupState.loading}>
              {backupState.loading ? (
                <><Spinner /> Generating backup…</>
              ) : (
                <><FiDownload /> Create Backup</>
              )}
            </ActionButton>

            {backupState.error && (
              <StatusBox $type="error">
                <FiAlertCircle /> {backupState.error}
              </StatusBox>
            )}

            {backupState.result && (
              <StatusBox $type="success">
                <FiCheckCircle />
                <div>
                  <div><strong>Backup ready.</strong> {backupState.result.filename}</div>
                  <Meta>
                    Size: {formatBytes(backupState.result.sizeBytes)} ·
                    Collections: {backupState.result.collectionCount} ·
                    Records: {backupState.result.totalDocs}
                  </Meta>
                  <DownloadLink href={backupState.result.url} download={backupState.result.filename}>
                    Download again
                  </DownloadLink>
                  <ExpiryNote><FiClock /> Link expires in 24 hours.</ExpiryNote>
                </div>
              </StatusBox>
            )}
          </Card>

          {/* Export Data (Excel) */}
          <Card as={motion.div} whileHover={{ y: -2 }}>
            <CardIcon $color={colors.success.main}>
              <FiFileText />
            </CardIcon>
            <CardTitle>Export Data</CardTitle>
            <CardDescription>
              Human-readable copy of your data as a <strong>single Excel workbook</strong>. One sheet per record type. Open it directly in Excel, Google Sheets, or LibreOffice.
            </CardDescription>
            <BulletList>
              <Bullet>For browsing, auditing, or sharing with your accountant.</Bullet>
              <Bullet>Nested data (e.g. invoice line items) is split into linked sheets.</Bullet>
              <Bullet>Not a recovery format — for that use "Create Backup".</Bullet>
            </BulletList>

            <ActionButton $secondary onClick={handleExportExcel} disabled={exportState.loading}>
              {exportState.loading ? (
                <><Spinner /> Generating Excel…</>
              ) : (
                <><FiDownload /> Export Data</>
              )}
            </ActionButton>

            {exportState.error && (
              <StatusBox $type="error">
                <FiAlertCircle /> {exportState.error}
              </StatusBox>
            )}

            {exportState.result && (
              <StatusBox $type="success">
                <FiCheckCircle />
                <div>
                  <div><strong>Export ready.</strong> {exportState.result.filename}</div>
                  <Meta>
                    Size: {formatBytes(exportState.result.sizeBytes)} ·
                    Sheets: {exportState.result.sheetCount}
                  </Meta>
                  <DownloadLink href={exportState.result.url} download={exportState.result.filename}>
                    Download again
                  </DownloadLink>
                  <ExpiryNote><FiClock /> Link expires in 24 hours.</ExpiryNote>
                </div>
              </StatusBox>
            )}
          </Card>
        </CardGrid>

        <FootNote>
          <FiAlertCircle />
          <span>
            Backups can take a few seconds to several minutes depending on the size of your data. The page may appear unresponsive while the file is being prepared — please wait until you see the success message.
          </span>
        </FootNote>
      </Container>
    </PageLayout>
  );
};

export default Backup;

// ─── Styled ──────────────────────────────────────────────
const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: ${spacing.xl};
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${colors.text.secondary};
  margin: 0;
  line-height: 1.5;
  max-width: 720px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
  gap: ${spacing.lg};
  margin-bottom: ${spacing.xl};
`;

const Card = styled.div`
  background: white;
  border-radius: ${borderRadius.lg};
  padding: ${spacing.xl};
  box-shadow: ${shadows.sm};
  border: 1px solid ${colors.border.light};
  display: flex;
  flex-direction: column;
`;

const CardIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${borderRadius.md};
  background: ${(p) => p.$color}15;
  color: ${(p) => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${spacing.md};

  svg {
    font-size: 28px;
  }
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.primary};
  margin: 0 0 8px 0;
`;

const CardDescription = styled.p`
  font-size: 13px;
  color: ${colors.text.secondary};
  line-height: 1.6;
  margin: 0 0 ${spacing.md} 0;
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${spacing.lg} 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Bullet = styled.li`
  font-size: 12px;
  color: ${colors.text.secondary};
  padding-left: 18px;
  position: relative;
  line-height: 1.5;

  &::before {
    content: '•';
    position: absolute;
    left: 4px;
    color: ${colors.primary.main};
    font-weight: bold;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background: ${(p) => p.$secondary
    ? `linear-gradient(135deg, ${colors.success.main}, ${colors.success.dark})`
    : `linear-gradient(135deg, ${colors.primary.main}, ${colors.primary.light})`};
  color: white;
  font-size: 14px;
  font-weight: ${typography.fontWeight.semibold};
  border: none;
  border-radius: ${borderRadius.md};
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  svg {
    font-size: 16px;
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const StatusBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: ${spacing.md};
  padding: ${spacing.md};
  border-radius: ${borderRadius.md};
  font-size: 13px;
  line-height: 1.5;
  background: ${(p) => p.$type === 'error' ? `${colors.error.main}10` : `${colors.success.main}10`};
  color: ${(p) => p.$type === 'error' ? colors.error.main : colors.success.dark};
  border: 1px solid ${(p) => p.$type === 'error' ? `${colors.error.main}30` : `${colors.success.main}30`};

  > svg {
    flex-shrink: 0;
    margin-top: 2px;
    font-size: 16px;
  }
`;

const Meta = styled.div`
  font-size: 11px;
  color: ${colors.text.secondary};
  margin-top: 4px;
`;

const DownloadLink = styled.a`
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
  color: ${colors.primary.main};
  font-weight: ${typography.fontWeight.semibold};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ExpiryNote = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 11px;
  color: ${colors.text.tertiary};

  svg { font-size: 12px; }
`;

const FootNote = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: ${spacing.md};
  background: ${colors.warning.main}10;
  border: 1px solid ${colors.warning.main}30;
  border-radius: ${borderRadius.md};
  font-size: 12px;
  color: ${colors.text.secondary};
  line-height: 1.5;

  svg {
    color: ${colors.warning.main};
    flex-shrink: 0;
    font-size: 16px;
    margin-top: 2px;
  }
`;
